import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabaseClient';

const NoticeDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const noticeId = searchParams?.get('id') || '';

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [activeImageUrl, setActiveImageUrl] = useState('');

  const getYoutubeVideoId = (rawUrl) => {
    const url = String(rawUrl || '').trim();
    if (!url) return '';
    const directId = url.match(/^[a-zA-Z0-9_-]{11}$/)?.[0];
    if (directId) return directId;
    try {
      const u = new URL(url);
      const host = (u.hostname || '').replace(/^www\./, '');
      if (host === 'youtu.be') return (u.pathname || '').replace('/', '').slice(0, 11);
      const v = u.searchParams.get('v');
      if (v) return v.slice(0, 11);
      const m = (u.pathname || '').match(/\/shorts\/([^/?#]+)/) || (u.pathname || '').match(/\/embed\/([^/?#]+)/);
      if (m?.[1]) return String(m[1]).slice(0, 11);
      return '';
    } catch {
      return '';
    }
  };

  const youtubeId = useMemo(() => getYoutubeVideoId(detail?.youtubeUrl), [detail?.youtubeUrl]);

  const decodeBase64Utf8 = (b64) => {
    const binary = atob(String(b64 || ''));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  };

  const blockImagePathSet = useMemo(() => {
    const set = new Set();
    try {
      const m = String(detail?.contentHtml || '').match(/<!--CH_BLOCKS:([\s\S]*?)-->/);
      if (!m?.[1]) return set;
      const parsed = decodeBase64Utf8(m[1]);
      const blocks = Array.isArray(parsed?.blocks) ? parsed.blocks : [];
      blocks.forEach((b) => {
        if (b?.imagePath) set.add(b.imagePath);
      });
      return set;
    } catch {
      return set;
    }
  }, [detail?.contentHtml]);

  const attachmentFiles = useMemo(
    () => (detail?.files || []).filter((f) => f?.filePath && !blockImagePathSet.has(f.filePath)),
    [detail?.files, blockImagePathSet]
  );

  const imageFiles = useMemo(
    () => (attachmentFiles || []).filter((f) => String(f?.contentType || '').startsWith('image/') && f?.publicUrl),
    [attachmentFiles]
  );

  const otherFiles = useMemo(
    () => (attachmentFiles || []).filter((f) => !String(f?.contentType || '').startsWith('image/') && f?.publicUrl),
    [attachmentFiles]
  );

  useEffect(() => {
    const load = async () => {
      if (!noticeId) {
        setIsLoading(false);
        setError('공지 ID가 없습니다.');
        return;
      }

      setIsLoading(true);
      setError('');
      setDetail(null);

      const baseQuery = supabase.from('ch_notices');

      let data;
      let err;

      ({ data, error: err } = await baseQuery
        .select('id, title, author_name, created_at, content_html, youtube_url, files:ch_notice_files(file_path, original_name, content_type, size_bytes)')
        .eq('id', noticeId)
        .eq('is_published', true)
        .maybeSingle());

      const errMsg = String(err?.message || '');
      const isYoutubeColumnMissing =
        errMsg.includes("Could not find the 'youtube_url'") ||
        errMsg.includes('column') && errMsg.includes('youtube_url') && errMsg.includes('does not exist');

      if (err && isYoutubeColumnMissing) {
        ({ data, error: err } = await baseQuery
          .select('id, title, author_name, created_at, content_html, files:ch_notice_files(file_path, original_name, content_type, size_bytes)')
          .eq('id', noticeId)
          .eq('is_published', true)
          .maybeSingle());
      }

      if (err || !data) {
        setError(err?.message || '공지사항을 불러오지 못했습니다.');
        setIsLoading(false);
        return;
      }

      const mappedFiles = (data.files || [])
        .filter((f) => f?.file_path)
        .map((f) => {
          const publicUrl = supabase.storage.from('notice-files').getPublicUrl(f.file_path).data.publicUrl;
          return {
            filePath: f.file_path,
            originalName: f.original_name || '첨부파일',
            contentType: f.content_type || '',
            sizeBytes: f.size_bytes || null,
            publicUrl,
          };
        });

      setDetail({
        id: data.id,
        title: data.title,
        authorName: data.author_name || '꿈현공장장',
        date: data.created_at ? String(data.created_at).slice(0, 10) : '',
        contentHtml: data.content_html || '',
        youtubeUrl: data.youtube_url || '',
        files: mappedFiles,
      });

      setIsLoading(false);
    };

    load();
  }, [noticeId]);

  return (
    <div className="min-h-screen bg-background">
      <Seo title={detail?.title ? detail.title : '공지사항'} canonicalPath="/notice-detail" />

      <header className="sticky top-0 z-navigation bg-card/90 backdrop-blur-sm border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-gaming">
            <Icon name="ArrowLeft" size={18} />
            <span className="text-sm font-medium">뒤로</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/login-registration')}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground shadow-gaming-sm hover:shadow-gaming-md transition-gaming"
          >
            로그인
          </button>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-8 md:py-10">
        {isLoading ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6">
            <p className="text-sm text-muted-foreground caption">로딩 중...</p>
          </div>
        ) : error ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : (
          <div className="bg-card rounded-lg shadow-gaming-md p-6 md:p-8 border border-border">
            <div className="mb-5">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">{detail?.title}</h1>
              <div className="text-xs md:text-sm text-muted-foreground caption">{detail?.date} · {detail?.authorName}</div>
            </div>

            {youtubeId ? (
              <div className="mb-6">
                <div className="aspect-video rounded-lg overflow-hidden border border-border bg-muted">
                  <iframe
                    title="공지 유튜브"
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${youtubeId}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            ) : null}

            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: detail?.contentHtml || '' }} />

            {(imageFiles.length > 0 || otherFiles.length > 0) && (
              <div className="pt-6 mt-6 border-t border-border">
                <div className="text-sm font-medium text-foreground mb-3">첨부파일</div>

                {imageFiles.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {imageFiles.map((f) => (
                      <button
                        key={f.filePath}
                        type="button"
                        className="w-full rounded-md overflow-hidden border border-border bg-background/50 hover:bg-background transition-gaming"
                        onClick={() => setActiveImageUrl(f.publicUrl)}
                      >
                        <div className="aspect-[4/3] bg-muted">
                          <img src={f.publicUrl} alt={f.originalName} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                        </div>
                        <div className="px-3 py-2 text-left">
                          <div className="text-xs text-muted-foreground caption truncate">{f.originalName}</div>
                          <div className="text-[11px] text-muted-foreground caption mt-1">클릭해서 크게 보기</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {otherFiles.length > 0 && (
                  <div className="space-y-2">
                    {otherFiles.map((f) => (
                      <a
                        key={f.filePath}
                        href={f.publicUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between gap-3 p-3 rounded-md border border-border bg-background/40 hover:bg-background transition-gaming"
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-foreground truncate">{f.originalName}</div>
                          <div className="text-xs text-muted-foreground caption mt-1">{f.contentType || 'file'}</div>
                        </div>
                        <div className="text-xs text-muted-foreground">열기</div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {activeImageUrl && (
        <div className="fixed inset-0 z-[1000] bg-background/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActiveImageUrl('')}>
          <div className="relative w-full max-w-5xl bg-card rounded-lg border border-border shadow-gaming-lg overflow-hidden" onClick={(e) => e?.stopPropagation()}>
            <button
              type="button"
              onClick={() => setActiveImageUrl('')}
              className="absolute top-3 right-3 z-10 p-2 rounded-md bg-background/80 hover:bg-background transition-gaming"
              aria-label="닫기"
            >
              <Icon name="X" size={18} className="text-foreground" />
            </button>
            <div className="max-h-[80vh] bg-muted flex items-center justify-center">
              <img src={activeImageUrl} alt="공지 이미지" className="w-full h-auto object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeDetail;
